import { Component } from '@theme/component';

/**
 * Drives the two-state Add button (choose a size -> add to cart) for the upsell
 * card. State is resolved directly from the clicked size radio's own data
 * attributes (data-variant-id / data-option-available) - no AJAX round trip is
 * needed since blocks/upsell.liquid only ever renders a single, non-swatch
 * option (e.g. Size).
 *
 * A MutationObserver re-applies our own text/class whenever the button changes
 * for any reason, including the shared product-form-component's morph of this
 * button with the fetched product page's own add-to-cart button (triggered by
 * the <variant-picker>'s background AJAX fetch). That morph would otherwise
 * silently replace our "before"/"after" colors with the PDP's button styling.
 *
 * @extends Component
 */
export class UpsellCardComponent extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;
  #applying = false;

  connectedCallback() {
    super.connectedCallback();

    const button = this.#getButton();
    if (!button) return;

    this.#observer = new MutationObserver(() => this.#applyState());
    this.#observer.observe(button, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @param {Event} event */
  handleOptionChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    this.#applyState();
  }

  /** @returns {HTMLButtonElement | null} */
  #getButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartButton"]'));
  }

  /** @returns {HTMLInputElement | null} */
  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="variantId"]'));
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #setButtonText(button, text) {
    if (!text) return;

    const textNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== ''
    );

    if (textNode) {
      textNode.textContent = text;
    } else {
      button.append(document.createTextNode(text));
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} addClasses
   * @param {string} removeClasses
   */
  #swapClasses(button, addClasses, removeClasses) {
    const toRemove = removeClasses.split(' ').filter(Boolean);
    const toAdd = addClasses.split(' ').filter(Boolean);

    if (toRemove.length) button.classList.remove(...toRemove);
    if (toAdd.length) button.classList.add(...toAdd);
  }

  #applyState() {
    if (this.#applying) return;

    const button = this.#getButton();
    if (!button) return;

    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    const variantIdInput = this.#getVariantIdInput();
    const chooseClasses = this.dataset.chooseClasses || '';
    const afterClasses = this.dataset.afterClasses || '';

    this.#applying = true;

    if (!checked) {
      if (variantIdInput) variantIdInput.value = '';
      button.disabled = true;
      this.#setButtonText(button, this.dataset.chooseText || '');
      this.#swapClasses(button, chooseClasses, afterClasses);
    } else {
      const available = checked.dataset.optionAvailable === 'true';
      const variantId = checked.dataset.variantId;

      if (variantIdInput && variantId) variantIdInput.value = variantId;
      button.disabled = !available;
      this.#setButtonText(button, available ? this.dataset.addText || '' : this.dataset.soldOutText || '');
      this.#swapClasses(button, afterClasses, chooseClasses);
    }

    requestAnimationFrame(() => {
      this.#applying = false;
    });
  }
}

if (!customElements.get('upsell-card')) {
  customElements.define('upsell-card', UpsellCardComponent);
}
